// Today's journal (feedback round four §C) — Henry's dated notes for the day,
// read by tomorrow's morning brief. Sits under Needs attention on Overview.
// Only today's entries are shown; the server rejects edits to older ones.
// Journal text is private: errors surface through the toast, never console.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { reportError } from '../../lib/errorEvents';
import { useIsMobile } from '../../lib/useIsMobile';
import { Group } from '../../components/primitives';
import { melbourneTime } from '../../../shared/dates';
import { JOURNAL_BODY_MAX, type JournalEntry } from '../../../shared/types';

function mergeEntries(known: JournalEntry[], incoming: JournalEntry[]): JournalEntry[] {
  return [...new Map([...known, ...incoming].map((entry) => [entry.id, entry])).values()]
    .sort((a, b) => a.created_at - b.created_at);
}

export function JournalSection() {
  const isMobile = useIsMobile();
  const [{ entries, today }, setJournal] = useState<{ entries: JournalEntry[]; today: string | null }>({ entries: [], today: null });
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState<{ id: string; dirty: boolean } | null>(null);
  const removedIds = useRef(new Set<string>());
  const todayRef = useRef(today);
  todayRef.current = today;

  const load = useCallback(() => {
    api<{ entries: JournalEntry[]; today: string }>('/api/admin/journal')
      .then((res) => setJournal((prev) => {
        // A response from before midnight must not restore yesterday's list.
        if (prev.today && res.today < prev.today) return prev;
        return {
          today: res.today,
          entries: mergeEntries(prev.entries, res.entries)
            .filter((entry) => entry.entry_date === res.today && !removedIds.current.has(entry.id)),
        };
      }))
      .catch((error) => reportError(error, 'Could not load today’s journal.'));
  }, []);
  useEffect(load, [load]);
  useEffect(() => { setEditor(null); }, [today]);
  useEffect(() => {
    const visible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('focus', load);
    return () => {
      document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  const add = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      const { entry } = await api<{ entry: JournalEntry }>('/api/admin/journal', { method: 'POST', json: { body } });
      setJournal((prev) => prev.today && entry.entry_date !== prev.today ? prev : {
        ...prev, entries: mergeEntries(prev.entries, [entry]),
      });
      if (entry.entry_date !== todayRef.current) load();
      setDraft('');
    } catch (error) {
      reportError(error, 'Could not save the journal entry.');
    } finally {
      setSaving(false);
    }
  };

  const replace = (entry: JournalEntry) => {
    setJournal((prev) => ({ ...prev, entries: prev.entries.map((e) => (e.id === entry.id ? entry : e)) }));
  };
  const remove = (id: string) => {
    removedIds.current.add(id);
    setJournal((prev) => ({ ...prev, entries: prev.entries.filter((e) => e.id !== id) }));
  };

  return (
    <Group
      title="Today’s journal"
      count={entries.length > 0 ? entries.length : undefined}
      link={isMobile ? undefined : <span className="jr-feed">→ feeds tomorrow’s brief</span>}
    >
      {entries.map((entry) => (
        <JournalRow
          key={entry.id}
          entry={entry}
          editing={editor?.id === entry.id}
          onEdit={() => setEditor((prev) => prev?.dirty ? prev : { id: entry.id, dirty: false })}
          onDirtyChange={(dirty) => setEditor((prev) => prev?.id === entry.id ? { ...prev, dirty } : prev)}
          onDone={() => setEditor((prev) => prev?.id === entry.id ? null : prev)}
          onConflict={() => { setEditor(null); load(); }}
          onSaved={replace}
          onRemoved={remove}
        />
      ))}
      <div className={`jr-add${focused ? ' focused' : ''}`}>
        <span className="plus">+</span>
        <input
          className="jr-input"
          value={draft}
          maxLength={JOURNAL_BODY_MAX}
          placeholder="What happened today? e.g. client pushed the shoot a week…"
          aria-label="Add a journal entry"
          enterKeyHint="done"
          readOnly={saving}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing && e.nativeEvent.keyCode !== 229) { e.preventDefault(); void add(); }
            if (e.key === 'Escape') setDraft('');
          }}
        />
        {!isMobile && focused && <span className="jr-hint">Enter to save</span>}
      </div>
      {isMobile && <div className="jr-foot">Private · read by tomorrow’s morning brief</div>}
    </Group>
  );
}

function JournalRow({
  entry,
  editing,
  onEdit,
  onDirtyChange,
  onDone,
  onConflict,
  onSaved,
  onRemoved,
}: {
  entry: JournalEntry;
  editing: boolean;
  onEdit: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onDone: () => void;
  onConflict: () => void;
  onSaved: (entry: JournalEntry) => void;
  onRemoved: (id: string) => void;
}) {
  const [text, setText] = useState(entry.body);
  const [busy, setBusy] = useState(false);
  // Two-step ×, same as the task rows: the first click arms, the second trashes.
  const [arming, setArming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) return;
    setText(entry.body);
    inputRef.current?.focus();
  }, [editing, entry.body]);

  useEffect(() => {
    if (!arming) return;
    const t = setTimeout(() => setArming(false), 4000);
    return () => clearTimeout(t);
  }, [arming]);

  const save = async () => {
    const body = text.trim();
    if (busy || !body) return;
    if (body === entry.body) { onDone(); return; }
    setBusy(true);
    try {
      const res = await api<{ entry: JournalEntry }>(`/api/admin/journal/${entry.id}`, { method: 'PATCH', json: { body } });
      onSaved(res.entry);
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) { setArming(false); onConflict(); }
      reportError(error, 'Could not update the journal entry.');
    } finally {
      setBusy(false);
    }
  };

  const trash = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/admin/journal/${entry.id}`, { method: 'DELETE' });
      onRemoved(entry.id);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) onConflict();
      reportError(error, 'Could not delete the journal entry.');
      setBusy(false);
      setArming(false);
    }
  };

  return (
    <div className={`row jr-row${editing ? ' editing' : ''}`}>
      <span className="jr-time">{melbourneTime(entry.created_at)}</span>
      {editing ? (
        <input
          ref={inputRef}
          className="jr-input"
          value={text}
          maxLength={JOURNAL_BODY_MAX}
          aria-label="Edit journal entry"
          enterKeyHint="done"
          disabled={busy}
          onChange={(e) => {
            setText(e.target.value);
            onDirtyChange(e.target.value.trim() !== entry.body);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing && e.nativeEvent.keyCode !== 229) { e.preventDefault(); void save(); }
            if (e.key === 'Escape') onDone();
          }}
          // Leaving an untouched editor closes it; a changed one stays open
          // so a stray click never throws the edit away.
          onBlur={() => { if (text.trim() === entry.body) onDone(); }}
        />
      ) : (
        <span className="row-title">{entry.body}</span>
      )}
      <span className="row-spacer" />
      {!editing && (
        <span className="jr-acts">
          <button type="button" onClick={onEdit}>Edit</button>
          <button
            type="button"
            className={arming ? 'armed' : undefined}
            title={arming ? 'Click again to remove' : 'Move to trash'}
            aria-label={arming ? 'Confirm remove journal entry' : 'Remove journal entry'}
            disabled={busy}
            onClick={() => (arming ? void trash() : setArming(true))}
          >
            {arming ? '✕' : '×'}
          </button>
        </span>
      )}
    </div>
  );
}
